export function MySQLEntitiesPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Entities - MySQL Template</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Understand TypeORM entities and how to define database models.
        </p>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold">What are Entities?</h2>
        <p className="text-muted-foreground leading-relaxed">
          Entities are classes that map to database tables. They define the structure of your data 
          and the relationships between different tables.
        </p>
      </div>

      <div className="bg-muted/30 rounded-xl p-8 space-y-6">
        <h2 className="text-2xl font-bold">User Entity Example</h2>
        <pre className="bg-muted p-6 rounded-lg overflow-x-auto text-sm">
          <code>{`import { Entity, PrimaryGeneratedColumn, Column, CreateDateColumn, UpdateDateColumn, OneToMany } from 'typeorm';
import { UserRole } from '@/shared/enums/user-role.enum';
import { RefreshToken } from './refresh-token.entity';

@Entity('users')
export class User {
  @PrimaryGeneratedColumn('uuid')
  id: string;

  @Column({ unique: true })
  email: string;

  @Column()
  password: string;

  @Column()
  firstName: string;

  @Column()
  lastName: string;

  @Column({
    type: 'enum',
    enum: UserRole,
    default: UserRole.USER,
  })
  role: UserRole;

  @OneToMany(() => RefreshToken, (token) => token.user, { cascade: true })
  refreshTokens: RefreshToken[];

  @CreateDateColumn()
  createdAt: Date;

  @UpdateDateColumn()
  updatedAt: Date;
}`}</code>
        </pre>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <FeatureCard
          title="Column Decorators"
          items={[
            "@Column() - Basic column",
            "@PrimaryGeneratedColumn() - Auto-increment ID",
            "@CreateDateColumn() - Auto timestamp on create",
            "@UpdateDateColumn() - Auto timestamp on update",
            "@Column({ unique: true }) - Unique constraint",
            "@Column({ type: 'enum' }) - Enum type"
          ]}
        />
        <FeatureCard
          title="Relationship Decorators"
          items={[
            "@OneToOne() - One-to-one relation",
            "@OneToMany() - One-to-many relation",
            "@ManyToOne() - Many-to-one relation",
            "@ManyToMany() - Many-to-many relation",
            "@JoinColumn() - Join column specification",
            "@JoinTable() - Join table for many-to-many"
          ]}
        />
      </div>

      <div className="space-y-6 mt-8">
        <h2 className="text-2xl font-bold">Relationship Example</h2>
        <div className="bg-card border rounded-xl p-6 space-y-4">
          <h3 className="text-lg font-semibold">OneToMany: User → RefreshTokens</h3>
          <pre className="bg-muted p-4 rounded text-sm overflow-x-auto">
            <code>{`// In User entity
@OneToMany(() => RefreshToken, (token) => token.user, { cascade: true })
refreshTokens: RefreshToken[];

// In RefreshToken entity
@ManyToOne(() => User, (user) => user.refreshTokens, { onDelete: 'CASCADE' })
@JoinColumn({ name: 'userId' })
user: User;`}</code>
          </pre>
        </div>
      </div>
    </div>
  )
}

function FeatureCard({ title, items }: { title: string, items: string[] }) {
  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex gap-2 text-sm text-muted-foreground">
            <span className="text-primary">•</span>
            <span className="font-mono text-xs">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

